import { useState } from 'react';
import styles from './ResponsiveTable.module.css';

const compareValues = (a, b) => {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { sensitivity: 'base', numeric: true });
};

// Single table implementation for all admin list views, CSS-only
// table-to-card collapse below 768px (data-label + ::before, no JS).
// Replaces the 6+ divergent table implementations found in the audit
// (Users.js/Invoices.js/Transactions.js's bare inline-styled <table>s vs.
// Items.js/Tickets.js's own one-off responsive CSS).
//
// Optional sorting: mark a column `sortable: true` and give it a
// `sortValue: (row) => comparable` accessor (falls back to row[col.key]) -
// clicking the header cycles asc -> desc -> unsorted.
//
// Optional row selection: pass `selectable`, a controlled `selectedKeys`
// Set, and `onSelectionChange(nextSet)` to get a checkbox column with a
// header "select all" checkbox.
export default function ResponsiveTable({
  columns, rows, rowKey = 'id', onRowClick, emptyMessage = 'No results.',
  selectable = false, selectedKeys, onSelectionChange, getRowClassName
}) {
  const [sort, setSort] = useState(null);

  if (!rows || rows.length === 0) {
    return <div className={styles.empty}>{emptyMessage}</div>;
  }

  const toggleSort = (col) => {
    if (!col.sortable) return;
    setSort((prev) => {
      if (!prev || prev.key !== col.key) return { key: col.key, direction: 'asc' };
      if (prev.direction === 'asc') return { key: col.key, direction: 'desc' };
      return null;
    });
  };

  let sortedRows = rows;
  if (sort) {
    const col = columns.find((c) => c.key === sort.key);
    if (col) {
      const getValue = col.sortValue || ((row) => row[col.key]);
      sortedRows = [...rows].sort((a, b) => compareValues(getValue(a), getValue(b)));
      if (sort.direction === 'desc') sortedRows.reverse();
    }
  }

  const toggleRow = (key) => {
    if (!onSelectionChange) return;
    const next = new Set(selectedKeys);
    if (next.has(key)) next.delete(key); else next.add(key);
    onSelectionChange(next);
  };

  const allSelected = selectable && sortedRows.length > 0 && sortedRows.every((row) => selectedKeys?.has(row[rowKey]));
  const toggleAll = () => {
    if (!onSelectionChange) return;
    onSelectionChange(allSelected ? new Set() : new Set(sortedRows.map((row) => row[rowKey])));
  };

  return (
    <table className={styles.table}>
      <thead>
        <tr>
          {selectable && (
            <th className={styles.checkboxCell}>
              <input type="checkbox" checked={allSelected} onChange={toggleAll} aria-label="Select all rows" />
            </th>
          )}
          {columns.map((col) => (
            <th
              key={col.key}
              className={col.sortable ? styles.sortableHeader : undefined}
              onClick={col.sortable ? () => toggleSort(col) : undefined}
            >
              {col.label}
              {col.sortable && (
                <i className={`fas ${sort?.key === col.key ? (sort.direction === 'asc' ? 'fa-sort-up' : 'fa-sort-down') : 'fa-sort'} ${styles.sortIcon}`}></i>
              )}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {sortedRows.map((row) => (
          <tr
            key={row[rowKey]}
            className={[onRowClick ? styles.clickableRow : '', getRowClassName?.(row) || ''].filter(Boolean).join(' ') || undefined}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
          >
            {selectable && (
              <td className={styles.checkboxCell} onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={!!selectedKeys?.has(row[rowKey])}
                  onChange={() => toggleRow(row[rowKey])}
                  aria-label="Select row"
                />
              </td>
            )}
            {columns.map((col) => (
              <td key={col.key} data-label={col.label}>
                {col.render ? col.render(row) : row[col.key]}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
